// Menu (drawing v2.6, screen 2 "Menu and tooltip"; reference section 7): 240
// wide, radius 10, sh2, items 40 tall (44 under 1024). A danger item last,
// after a divider, in red; a disabled item says why at its right ("pick 2
// first"). The trigger is a real button with aria-haspopup; the list is
// role="menu"; Esc and an outside click close it and focus returns to the
// trigger; arrows move between items.

import { cloneElement, isValidElement, useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from "react";
import { cx } from "./cx";

export interface MenuItem {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  onSelect?: () => void;
  to?: string;
  danger?: boolean;
  /** Disabled, with the reason shown at the item's right. */
  disabledReason?: string;
  separatorBefore?: boolean;
  title?: string;
}

type TriggerProps = { onClick?: ((e: React.MouseEvent) => void) | undefined; "aria-haspopup"?: string | undefined; "aria-expanded"?: boolean | undefined; "aria-controls"?: string | undefined };

export function Menu({ items, trigger, align = "right", label }: { items: MenuItem[]; /** An IconButton or Button; the menu adds the aria wiring. */ trigger: ReactElement<TriggerProps>; align?: "left" | "right"; label?: string | undefined }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        (rootRef.current?.querySelector("[aria-haspopup]") as HTMLElement | null)?.focus();
      }
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    // Focus the first item on open.
    window.requestAnimationFrame(() => itemRefs.current.find(Boolean)?.focus());
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function move(from: number, dir: 1 | -1) {
    const n = items.length;
    for (let step = 1; step <= n; step++) {
      const i = (from + dir * step + n) % n;
      if (!items[i]?.disabledReason) {
        itemRefs.current[i]?.focus();
        return;
      }
    }
  }

  const triggerEl = isValidElement<TriggerProps>(trigger)
    ? cloneElement<TriggerProps>(trigger, {
        "aria-haspopup": "menu",
        "aria-expanded": open,
        ...(open ? { "aria-controls": menuId } : {}),
        onClick: (e: React.MouseEvent) => {
          trigger.props.onClick?.(e);
          setOpen((o) => !o);
        },
      })
    : trigger;

  return (
    <div className="pg-menu__wrap" ref={rootRef}>
      {triggerEl}
      {open && (
        <div className={cx("pg-menu__pop", align === "left" && "pg-menu__pop--left")}>
          <div className="pg-menu" role="menu" id={menuId} aria-label={label}>
            {items.map((it, i) => {
              const disabled = Boolean(it.disabledReason);
              const common = {
                role: "menuitem" as const,
                className: cx("pg-menu__item", it.danger && "pg-menu__item--dg"),
                "aria-disabled": disabled || undefined,
                title: it.title,
                tabIndex: -1,
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    move(i, 1);
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    move(i, -1);
                  }
                },
              };
              const body = (
                <>
                  {it.icon}
                  {it.label}
                  {it.disabledReason && <small>{it.disabledReason}</small>}
                </>
              );
              return (
                <span key={it.key} style={{ display: "contents" }}>
                  {it.separatorBefore && <hr />}
                  {it.to && !disabled ? (
                    <a
                      {...common}
                      href={it.to}
                      ref={(el) => {
                        itemRefs.current[i] = el;
                      }}
                      onClick={() => setOpen(false)}
                    >
                      {body}
                    </a>
                  ) : (
                    <button
                      {...common}
                      type="button"
                      ref={(el) => {
                        itemRefs.current[i] = el;
                      }}
                      onClick={() => {
                        if (disabled) return;
                        setOpen(false);
                        it.onSelect?.();
                      }}
                    >
                      {body}
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
